import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, priceLabel, locationLabel, StatusBadge, dealScheme } from "@/components/StatusBadge";
import CoopItemPicker from "@/components/CoopItemPicker";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Store, Tag, ShieldCheck, ShoppingCart, HandCoins, Users, ArrowRight, LogIn, MapPin, CalendarClock, UserCheck } from "lucide-react";

export default function DealDetail() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [slots, setSlots] = useState(2);
  const [selectedItems, setSelectedItems] = useState(new Set());

  const load = async () => {
    const { data } = await api.get(`/deals/${dealId}`);
    setData(data);
    const items = data?.deal?.line_items || [];
    if (items.length) setSelectedItems(new Set(items.map((it) => it.item_id)));
  };

  useEffect(() => {
    load();
  }, [dealId]);

  const toggleItem = (id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const showInterest = async () => {
    if (!user) {
      toast.info("Please log in to claim this deal");
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const isMulti = data?.deal && dealScheme(data.deal) === "multipack";
      const { data: match } = await api.post(`/deals/${dealId}/interest`, isMulti ? { slots } : {});
      toast.success(isMulti && slots > 1
        ? `You're in! Opened ${slots} partner slots.`
        : "You're in! Waiting for a partner to join.");
      navigate(`/room/${match.match_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const joinMatch = async (matchId) => {
    if (!user) {
      toast.info("Please log in to join this deal");
      navigate("/login");
      return;
    }
    const coopItems = data?.deal?.line_items || [];
    const isCoop = coopItems.length > 0;
    if (isCoop && selectedItems.size === 0) {
      toast.error("Pick at least one item you want");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/matches/${matchId}/join`, isCoop ? { selected_items: [...selectedItems] } : {});
      toast.success("Joined as partner!");
      navigate(`/room/${matchId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  const { deal, open_matches, my_match } = data;

  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        <button
          onClick={() => navigate("/browse")}
          data-testid="back-to-browse"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to board
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="mt-6 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 overflow-hidden rounded-xl border border-black/10 bg-white hard-shadow-sm">
            <img src={deal.image_url} alt={deal.product_name} className="w-full h-72 object-cover" />
            <div className="p-6">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Store className="h-3.5 w-3.5" /> {deal.retailer}
                <span>·</span> <Tag className="h-3.5 w-3.5" /> {deal.category}
                {deal.source && <><span>·</span> from {deal.source}</>}
              </div>
              <h1 className="mt-2 font-heading text-3xl tracking-tight" style={{ fontWeight: 700 }}>{deal.title}</h1>
              <Badge className="mt-3 bg-primary text-primary-foreground border-none">{deal.deal_type}</Badge>
              <p className="mt-4 text-muted-foreground leading-relaxed">{deal.description}</p>

              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="h-4 w-4" /> {locationLabel(deal)}{deal.location_scope === "city" && deal.address ? ` · ${deal.address}` : ""}</span>
                {deal.expires_at && <span className="flex items-center gap-1.5 text-muted-foreground"><CalendarClock className="h-4 w-4" /> Expires {deal.expires_at}</span>}
              </div>

              {deal.both_present_required && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <UserCheck className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">Both the buyer and the deal partner must be present together in-store when the item(s) are purchased. The partner may waive this in the deal room.</p>
                </div>
              )}

              {deal.terms && (
                <div className="mt-4 rounded-lg border border-black/10 bg-secondary p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Terms</p>
                  <p className="mt-1 text-sm">{deal.terms}</p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-5">
            {/* price split */}
            <div className="rounded-xl border border-black/10 bg-white p-6 hard-shadow-sm">
              <p className="text-sm text-muted-foreground">The buyer pays full price</p>
              <p className="font-heading text-3xl" style={{ fontWeight: 700 }}>{priceLabel(deal)}</p>
              <div className="mt-4 flex items-center gap-3 rounded-lg bg-accent/10 p-3">
                <HandCoins className="h-5 w-5 text-accent" />
                <p className="text-sm">
                  Split it: the partner pays an agreed fee for the second item, so <b>both save</b>.
                </p>
              </div>
            </div>

            {/* action */}
            <div className="rounded-xl border border-black/10 bg-white p-6">
              {!user && (
                <div
                  data-testid="login-to-claim-banner"
                  className="mb-5 rounded-lg border border-primary/20 bg-primary/5 p-4"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <LogIn className="h-4 w-4" /> Log in to claim this deal
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Create a free account to become the buyer or join as a partner and start splitting.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      data-testid="banner-login-btn"
                      onClick={() => navigate("/login")}
                      className="rounded-full"
                    >
                      Log in
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="banner-signup-btn"
                      onClick={() => navigate("/signup")}
                      className="rounded-full border-black/20"
                    >
                      Sign up free
                    </Button>
                  </div>
                </div>
              )}
              {my_match ? (
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">Your deal room</span>
                    <StatusBadge status={my_match.status} />
                  </div>
                  <Button
                    data-testid="open-room-btn"
                    className="mt-4 w-full rounded-full"
                    onClick={() => navigate(`/room/${my_match.match_id}`)}
                  >
                    Open deal room <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <ShoppingCart className="h-4 w-4 text-primary" /> Be the buyer
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    You'll purchase the deal in-store and receive a fee from your partner.
                  </p>
                  {dealScheme(deal) === "multipack" && (
                    <div data-testid="multipack-slots" className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-900">
                        <Users className="h-4 w-4" /> Split this pack with how many partners?
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            data-testid={`slot-option-${n}`}
                            onClick={() => setSlots(n)}
                            className={`h-9 w-9 rounded-full text-sm font-semibold transition-colors ${
                              slots === n ? "bg-blue-600 text-white" : "bg-white text-blue-800 border border-blue-200 hover:bg-blue-100"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                      <p className="mt-2 text-[12px] text-blue-800">
                        Opens {slots} partner slot{slots === 1 ? "" : "s"} — each partner pays their share so a big pack can be split several ways.
                      </p>
                    </div>
                  )}
                  <Button
                    data-testid="show-interest-btn"
                    disabled={busy}
                    className="mt-3 w-full rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200"
                    onClick={showInterest}
                  >
                    {dealScheme(deal) === "multipack" && slots > 1
                      ? `I'm interested — open ${slots} slots`
                      : "I'm interested — start a split"}
                  </Button>

                  {open_matches.length > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <Users className="h-4 w-4 text-accent" /> Buyers looking for a partner
                      </div>
                      {deal.line_items?.length > 0 && (
                        <div className="mt-3">
                          <CoopItemPicker items={deal.line_items} selected={selectedItems} onToggle={toggleItem} />
                        </div>
                      )}
                      <div className="mt-3 space-y-2">
                        {open_matches.map((m) => (
                          <div key={m.match_id} className="flex items-center justify-between rounded-lg border border-black/10 p-3">
                            <span className="text-sm">{m.primary_name} needs a partner</span>
                            <Button
                              size="sm" variant="outline" disabled={busy}
                              data-testid={`join-match-${m.match_id}`}
                              onClick={() => joinMatch(m.match_id)}
                              className="rounded-full"
                            >
                              Join
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-accent shrink-0" />
                Funds are released to the buyer only after the partner confirms the redemption code.
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
